import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Checkbox, Typography, CircularProgress, Chip } from '@mui/material';
import CustomListbox from '../../../components/CustomListbox';
import {
  isBusinessProfileComplete,
  OVERALL_BUSINESS_PROFILE_STATUS_LABELS,
} from '../../../constants/businessProfileStatusConstants.jsx';

const getProfileOptionLabel = (option) => {
  const name = option?.party_name || option?.name || '';
  const email = option?.email || '';
  const tin = option?.tin || option?.party_tax_scheme?.company_id || '';
  return [name, email, tin].filter(Boolean).join(' | ');
};

const BusinessProfileMultiSelect = ({
  profiles = [],
  selected = [],
  isLoading = false,
  disabled = false,
  onChange,
  allProfilesLabel = 'All Business Profiles',
  selectedCountLabel,
  selectAllLabel,
  label,
  required = false,
  error = false,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const selectedObjects = useMemo(() => {
    return profiles.filter((profile) => selected.includes(profile.profile_id));
  }, [profiles, selected]);

  const countLabel =
    selectedCountLabel?.replace('{count}', String(selectedObjects.length)) ??
    `${selectedObjects.length} Business Profile(s) selected`;

  const closedLabel = selectedObjects.length === 0 ? allProfilesLabel : countLabel;

  const resolvedSelectAllLabel = (selectAllLabel ?? `Select All ${profiles.length} business profiles`).replace(
    '{count}',
    String(profiles.length),
  );

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={profiles}
        loading={isLoading}
        disabled={disabled || isLoading}
        value={selectedObjects}
        inputValue={inputValue}
        disableCloseOnSelect
        filterOptions={(options) => options}
        ListboxComponent={CustomListbox}
        ListboxProps={{
          selectAllLabel: resolvedSelectAllLabel,
          onSelectAll: () => onChange(profiles.map((profile) => profile.profile_id)),
          onClearAll: () => onChange([]),
          sx: {
            paddingTop: 0,
            maxHeight: 256,
            overflowY: 'auto',
          },
        }}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          }
        }}
        getOptionLabel={getProfileOptionLabel}
        isOptionEqualToValue={(option, value) => option.profile_id === value.profile_id}
        onChange={(_, newValue) => {
          onChange(newValue.map((profile) => profile.profile_id));
        }}
        renderTags={() => {
          if (open) return null;
          return <span style={{ marginLeft: 8 }}>{closedLabel}</span>;
        }}
        renderOption={(props, option, { selected: isSelected }) => {
          const isIncomplete = !isBusinessProfileComplete(option);

          return (
            <li {...props}>
              <Checkbox checked={isSelected} sx={{ marginRight: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontSize={14} fontWeight={500}>
                  {getProfileOptionLabel(option)}
                </Typography>
                {isIncomplete && (
                  <Chip
                    label={OVERALL_BUSINESS_PROFILE_STATUS_LABELS.ONBOARDED}
                    size="small"
                    color="info"
                    sx={{ marginLeft: 'auto', fontSize: '10px', height: 20 }}
                  />
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={helperText}
            placeholder={open ? 'Search Business Profile' : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BusinessProfileMultiSelect;
